// ProfileImage.js
import React, { useState, useEffect } from 'react';
import { User, X, Camera } from 'lucide-react';
import { usersApi } from 'api';
import { ESPORAI_MAX_FILE_SIZE, MB } from 'constants/utils';

const ProfileImage = ({ profileData, onImageUpdate }) => {
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [imageError, setImageError] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  // Efecto para inicializar la imagen
  useEffect(() => {
    if (profileData?.profile_image) {
      setImagePreview(profileData.profile_image);
      setImageError(false);
    } else {
      setImagePreview(null);
      setImageError(false);
    }
  }, [profileData?.profile_image]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Clear any previous errors
    setError('');

    // Validaciones de archivo
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError('Por favor selecciona una imagen en formato JPG, PNG o GIF');
      return;
    }

    // Validar el tamaño del archivo (5MB)
    if (file.size > ESPORAI_MAX_FILE_SIZE) {
      setError(`La imagen no debe superar los ${ESPORAI_MAX_FILE_SIZE / MB} MB`);
      return;
    }

    // Crear preview inmediato
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setImageError(false);
    };
    reader.readAsDataURL(file);

    try {
      setUploadingImage(true);
      const response = await usersApi.uploadProfilePicture(file);
      const newProfileImageUrl = response?.data?.media?.url;
      if (newProfileImageUrl) {
        setImagePreview(newProfileImageUrl);
        onImageUpdate(newProfileImageUrl);
      } else {
        throw new Error('No se recibió la URL de la imagen del servidor');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.response?.data?.error || err.message || 'Error actualizando foto de perfil');
    } finally {
      setUploadingImage(false);
      // Clear the file input to allow selecting the same file again
      e.target.value = '';
    }
  };

  const handleDeleteImage = async () => {
    try {
      setUploadingImage(true);
      await usersApi.deleteProfilePicture();
      setImagePreview(null);
      onImageUpdate(null);
      setError('');
    } catch (err) {
      console.error('Error deleting image:', err);
      setError(err.response?.data?.error || err.message || 'Error eliminando foto de perfil');
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <div className="flex flex-col items-start gap-2">
      <div 
        className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center relative group"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {imagePreview && !imageError ? (
          <>
            <img
              src={imagePreview}
              alt="Profile"
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
            {isHovering && !uploadingImage && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <label
                  htmlFor="profile-photo-input"
                  className="w-6 h-6 bg-[#0F31F7] text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-[#0F31F7] bg-opacity-90 transition-colors duration-150"
                >
                  <Camera className="w-3 h-3" />
                </label>
                <button
                  onClick={handleDeleteImage}
                  className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors duration-150"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <User className="w-6 h-6 text-gray-400" />
            </div>
            {isHovering && !uploadingImage && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <label
                  htmlFor="profile-photo-input"
                  className="w-6 h-6 bg-[#0F31F7] text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-[#0F31F7] bg-opacity-90 transition-colors duration-150"
                >
                  <Camera className="w-3 h-3" />
                </label>
              </div>
            )}
          </>
        )}
        {uploadingImage && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white"></div>
          </div>
        )}
      </div>
      <input
        type="file"
        accept="image/*"
        onChange={handlePhotoUpload}
        className="hidden"
        id="profile-photo-input"
        disabled={uploadingImage}
      />
      {error && (
        <div className="text-xs text-red-500">
          {error}
        </div>
      )}
    </div>
  );
};

export default ProfileImage;